import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BodyText,
  Box,
  Chip,
  MaterialSymbol,
  SideDrawer,
  Tooltip,
  withStyles,
  type AccountUserRole,
  type PermissionsMap,
  type UserRoleAssignment,
} from 'vopay';
import { Button } from '#shared/components/UI';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import { useToast } from '#shared/providers/Toast';
import useUserRoleAssignment from '../useUserRoleAssignment';
import PermissionsSearchField from './PermissionsSearchField';
import UserPermissionsForm, { PermissionFormValues, UserPermissionsFormRef } from './UserPermissionsForm';
import styles from './UserPermissionsDrawer.module.scss';
import useUserPermissions from './useUserPermissions';

const cn = withStyles(styles);

type UserPermissionsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  emailAddress?: string;
  /**
   * The account's full role list (with each role's permission keys), to work out which permissions the
   * user's assigned roles already grant. Without it, no permission is locked.
   */
  availableRoles?: AccountUserRole[];
};

// Edits one user's individual permissions. Permissions granted by the user's assigned roles show as on
// and locked, and are not saved from here.
export default function UserPermissionsDrawer({
  isOpen,
  onClose,
  emailAddress,
  availableRoles = [],
}: UserPermissionsDrawerProps) {
  const { fetchUserPermissions, saveUserPermissions } = useUserPermissions();
  const { fetchUserRoleAssignments } = useUserRoleAssignment();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.UserPermissionsDrawer' });
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  const toast = useToast();
  const formRef = useRef<UserPermissionsFormRef | null>(null);
  const [permissions, setPermissions] = useState<PermissionsMap>({});
  const [assignedRoles, setAssignedRoles] = useState<UserRoleAssignment[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAllEnabled, setIsAllEnabled] = useState(false);
  // The form mounts only once both fetches are in: formik doesn't re-initialize when initialValues
  // change, so it needs the correct lock set from the start.
  const [isDataReady, setIsDataReady] = useState(false);
  const isSearching = searchTerm.trim().length > 0;

  // Union of the permission keys granted by each assigned role.
  const lockedPermissions = useMemo(() => {
    const keys = new Set<string>();

    assignedRoles.forEach((assigned) => {
      availableRoles
        .find((role) => role.RoleID === assigned.RoleID)
        ?.Permissions.forEach((permissionKey) => keys.add(permissionKey));
    });

    return keys;
  }, [assignedRoles, availableRoles]);

  useEffect(() => {
    if (!emailAddress || !isOpen) {
      setAssignedRoles([]);
      setIsDataReady(false);

      return;
    }

    let isStale = false;

    async function load(email: string) {
      blockingLoader.show();

      try {
        setIsDataReady(false);

        const [fetchedPermissions, fetchedRoles] = await Promise.all([
          fetchUserPermissions({ EmailAddress: email }),
          // Already logged; a failed role lookup just means nothing is locked.
          fetchUserRoleAssignments({ Username: email }).catch(() => []),
        ]);

        if (isStale) return;

        setPermissions(fetchedPermissions);
        setAssignedRoles(fetchedRoles);
        setIsDataReady(true);
      } catch (error) {
        if (isStale) return;

        const errorMessage =
          error instanceof Error
            ? error.message
            : t('Errors.fetchPermissions', {
                defaultValue: 'Failed to load the user’s permissions. Please try again.',
              });

        alertDialog.error(t('Errors.title', { defaultValue: 'Error' }), errorMessage);
      } finally {
        blockingLoader.hide();
      }
    }

    load(emailAddress);

    return () => {
      isStale = true;
    };
  }, [emailAddress, isOpen]);

  async function handleSave(values: PermissionFormValues) {
    if (!emailAddress) return;

    blockingLoader.show();

    try {
      await saveUserPermissions({ EmailAddress: emailAddress, values });
      onClose();
      toast.success(t('Success.permissionsUpdated', { defaultValue: 'User permissions updated successfully.' }));
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('Errors.savePermissions', { defaultValue: 'Failed to save permissions. Please try again.' });

      alertDialog.error(t('Errors.title', { defaultValue: 'Error' }), errorMessage);
    } finally {
      blockingLoader.hide();
    }
  }

  function handleFormStateChange(nextHasChanges: boolean, nextIsValid: boolean) {
    setHasChanges(nextHasChanges);
    setIsValid(nextIsValid);
  }

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Box flx="col">
          <span>{t('title', { defaultValue: 'Edit Permissions' })}</span>
          {emailAddress && <BodyText weight="md">{emailAddress}</BodyText>}
        </Box>
      }
      headerFullWidthContent={<PermissionsSearchField value={searchTerm} onChange={setSearchTerm} />}
      footer={
        <Box jc="fs" gap="sm" className={cn('footer')}>
          {!isSearching && (
            <Button onClick={() => formRef.current?.toggleAll(!isAllEnabled)} colorScheme="secondary">
              {isAllEnabled
                ? t('disableAllButton', { defaultValue: 'Disable All' })
                : t('enableAllButton', { defaultValue: 'Enable All' })}
            </Button>
          )}

          <Button
            disabled={!isDataReady || !hasChanges || !isValid}
            colorScheme="primary"
            form="userPermissionsForm"
            type="submit"
          >
            {t('saveButton', { defaultValue: 'Save Permissions' })}
          </Button>
        </Box>
      }
    >
      {isDataReady && (
        <>
          {assignedRoles.length > 0 && (
            <div className={cn('rolesAssignedRow')}>
              <div className={cn('rolesAssignedLabel')}>
                <span>{t('rolesAssigned', { defaultValue: 'Role(s) Assigned' })}</span>

                <Tooltip
                  type="hover"
                  position="right"
                  hasButton={false}
                  customElement={<MaterialSymbol icon="info" size="16px" color="rgba(45, 45, 45, 0.5)" />}
                  information={t('rolesAssignedTooltip', {
                    defaultValue:
                      "Permissions from assigned roles appear as enabled and can't be toggled here. To remove them, unassign the role from Actions → Manage Role(s).",
                  })}
                />
              </div>

              <div className={cn('rolesAssignedChips')}>
                {assignedRoles.map((role) => (
                  <Chip key={role.RoleID} label={role.Name} />
                ))}
              </div>
            </div>
          )}

          <UserPermissionsForm
            ref={formRef}
            id="userPermissionsForm"
            searchTerm={searchTerm}
            onSubmit={handleSave}
            permissions={permissions}
            lockedPermissions={lockedPermissions}
            onFormStateChange={handleFormStateChange}
            onAllEnabledChange={setIsAllEnabled}
          />
        </>
      )}
    </SideDrawer>
  );
}
